//@flow
import { ContentService } from "openbet-content-api-sdk";
import { contentApiSchema } from "./contentApiSchema";
import { normalize } from "normalizr";
import filter from "./filter";
import type { ContentApi, ContentMarket, ApiConfig } from "bb-api-sdk";
import pick from "lodash.pick";
import isNil from "lodash.isnil";
import isEmpty from "lodash.isempty";

/**
 * An implementation of the ContentAPI, which uses the OB Content API as the
 * communication layer.
 *
 * @ignore
 */
export default class SGContentService implements ContentApi {
  /**
   * Update outcomes which are linked to handicap markets to display both the outcome name and handicap value.
   *
   * @param outcome
   * @returns {string}
   */
  formatMarketName = (outcome: ContentOutcome) => {
    if (!isNil(outcome.prices) && !isNil(outcome.prices[0].handicapHigh) && !['LOWER', 'HIGHER'].includes(outcome.meaningCode)) {
      return `${outcome.name} ${outcome.prices[0].handicapHigh}`;
    }
    return outcome.name;
  };

  /**
   * Sort array of markets by the order they appear in categoryFilter array
   *
   * @param markets
   * @param categoryFilter
   * @returns {Array}
   */
  displayByCategoryOrder(markets, categoryFilter) {
    return markets.sort((a, b) => {
      const aGroupCode = !isNil(a.groupCode) && a.groupCode.split("_").join(" ").toLowerCase();
      const bGroupCode = !isNil(b.groupCode) && b.groupCode.split("_").join(" ").toLowerCase();
      return (
        categoryFilter.indexOf(aGroupCode) -
          categoryFilter.indexOf(bGroupCode) ||
        a.name.localeCompare(b.name) ||
        a.handicapValue - b.handicapValue
      );
    });
  }

  /**
   * Given a list of market names (categories), can this market be categorised
   *
   * @param market
   * @param categories
   * @param filteringType
   * @returns {boolean}
   */
  isMarketCategorisedBy(market: ContentMarket, categories: [string], filteringType): boolean {
    //market.groupCode    
    const matched = filter(market.groupCode, categories, filteringType);
    return matched.length > 0;
  }
  /**
   * Determine if the fallback widget will be displayed by inspecting content data
   *
   * @param market
   * @returns {boolean}
   */
  shouldDisplayFallback(market: ContentMarket) {
    return isEmpty(market)
      ? true
      : Object.values(market.events).filter(event => event.liveNow)
          .length > 0 || Object.keys(market.markets).length === 0;
  }

  /**
   * Formats selection data to be sent to the betslip.
   *
   * @param outcome
   * @param market
   * @returns {object}
   */
  formatSelection(outcome: ContentOutcome, market: ContentMarket) {    
    return {
      selectionId: outcome.id,
    };
  }

  /**
   *  Formats the selection title depending on the outcome type
   *
   * @param outcome
   * @param market
   * @returns {string}
   */
  filterSelectionTitle(outcome: ContentOutcome, market: ContentMarket) {
    if (market.subType === "CS") {
      const { home, away } = outcome.outcomeScore;
      return home < away ? `${away} - ${home}` : `${home} - ${away}`;
    } else if (outcome.type === "WH") {
      // Ensure handicap values display the correct sign (Positive or negative)
      const isPositive = market.handicapValue > 0;
      const sign = isPositive ? { H: "+", A: "-"} : { H: "-", A: "+" };
      let value = market.handicapValue.toString();
      value = `${sign[outcome.subType]}${value.split("-").join("")}`;
      return `${outcome.name} ${value}`;
    } 
    // else if (["H", "A"].includes(outcome.subType)) {
    //   return outcome.name.split("-")[0];
    // }
    return outcome.name;
  }

  /**
   *  Formats central header, when there are three columns (Useally correct score markets)
   *
   * @param outcomes
   * @returns {string}
   */
  centralColumnTitle(outcomes) {
    return !isNil(outcomes[0].name) ? outcomes[0].name.split(" ")[0] : null;
  }

  /**
   * Gets teams object from market data
   *
   * @param event
   * @param market
   * @returns {Array}
   */
  getTeamNames(event: ContentEvent, market: ContentMarket, outcomes) {
    const teamSubTypes = ["H", "A", "CS", "S"];
    const outcome = outcomes.length !== 0 ? outcomes[0][0] : undefined;
    const teamNames = Object.values(pick(event, market.eventId))[0].teams.map(team => team.name);
    const displayTeamNames =
      outcome !== undefined &&
      teamNames.some(
        teamName =>
          teamSubTypes.includes(outcome.subType)
      );
    return displayTeamNames ? teamNames : null;
  }

  /**
   * Fetches the BetBuilder markets for a given eventId
   *
   * @param config
   * @param eventId
   * @returns {Promise<R>|Promise<R|*>|Promise<*>}
   */
  fetchBuilderMarkets(config: ApiConfig, eventId: string, locale: string) {
    if (config == null)
      throw Error("Must configure bb-content-api before loading data");

    const MarketsRequest = {
      query: "events-by-ids",
      operationName: undefined,
      variables: {
        eventIds: eventId,
        includeChildMarkets: true,
        fixedOddsAvailable: false,
        rcpAvailable: true,
        active: true
      }
    };

    const service = new ContentService({
      endpoint: config.endpoint,
      headers: {["X-Accept-Language"]: locale}
    });
    
    return service
      .executeNamedQuery(MarketsRequest.query, MarketsRequest.variables)
      .then(response => response.json())
      .then(json => normalize(json.data, contentApiSchema).entities);
  }

  /**
   * Fetches the details of outcomes given in the list of outcome Ids
   *
   * @param config
   * @param outcomeIds
   * @returns {Promise<R>|Promise<R|*>|Promise<*>}
   */
  fetchDetailsForOutcomes(config: ApiConfig, outcomeIds: [string]) {
    if (config == null)
      throw Error("Must configure bb-content-api before loading data");

    const MarketsRequest = {
      query: "events-by-outcomeIds",
      operationName: undefined,
      variables: {
        outcomeIds,
        includeChildMarkets: true,
        rcpAvailable: true,
        fixedOddsAvailable: false
      }
    };

    const service = new ContentService({
      endpoint: config.endpoint
    });

    return service
      .executeNamedQuery(MarketsRequest.query, MarketsRequest.variables)
      .then(response => response.json())
      .then(json => normalize(json.data, contentApiSchema).entities);
  }
}



// WEBPACK FOOTER //
// ./src/apis/content/index.js